import { CheckIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import LogoIcon from "@/core/components/logo-icon";
import { Button } from "@/core/components/ui/button";
import { Separator } from "@/core/components/ui/separator";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { formatMoneyCents } from "@/core/utils/money";
import { cn } from "@/core/utils/utils";
import { InitiateVerificationResponse } from "@/payouts/payouts";

interface SelectPayoutTypeProps {
  verificationData: InitiateVerificationResponse;
  onSelectPaymentType: (type: "credit" | "cash") => void;
}

export const SelectPayoutType = ({
  verificationData,
  onSelectPaymentType,
}: SelectPayoutTypeProps) => {
  const [selectedPaymentType, setSelectedPaymentType] = useState<
    "credit" | "cash" | null
  >(null);

  const handleSelect = () => {
    if (!selectedPaymentType) return;
    onSelectPaymentType(selectedPaymentType);
  };

  return (
    <Stack
      align="center"
      fullWidth
      className="mx-auto min-h-screen max-w-md bg-card p-8"
      gap="lg"
    >
      <LogoIcon />
      <Typography
        variant="subtitleW1"
        className="mx-auto w-[311px] text-center"
      >
        Choose how to recieve your{" "}
        {formatMoneyCents(verificationData?.payout_amount ?? 0)} referral
        payment
      </Typography>
      <Stack gap="none" justify="center" fullWidth>
        <PayoutSelectionListItem
          title="Cash payment"
          onClick={() => setSelectedPaymentType("cash")}
          selected={selectedPaymentType === "cash"}
        />
        <Separator />
        <PayoutSelectionListItem
          title={`Credit with ${verificationData?.business_name}`}
          onClick={() => setSelectedPaymentType("credit")}
          selected={selectedPaymentType === "credit"}
        />
      </Stack>
      <Button onClick={handleSelect} fullWidth disabled={!selectedPaymentType}>
        Next
      </Button>
      <Typography variant="captionW1" className="text-center text-[#637381]">
        For cash payments you will be redirected to a third party payment
        provider.
      </Typography>
    </Stack>
  );
};

interface PayoutSelectionListItemProps {
  title: string;
  onClick: () => void;
  selected?: boolean;
}

const PayoutSelectionListItem = ({
  title,
  onClick,
  selected = false,
}: PayoutSelectionListItemProps) => (
  <div
    role="button"
    onClick={onClick}
    className={cn("flex w-full items-center justify-between p-4", {
      "bg-blue-5": selected,
    })}
  >
    <Typography variant="bodyW2">{title}</Typography>
    {selected && <CheckIcon className="h-4 w-4" />}
  </div>
);
